import { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Lock, Check, Coins } from "lucide-react";
import { API, AuthContext } from "../App";
import { avatarDataUri, DEFAULT_AVATAR_OPTIONS } from "../utils/avatar";
import Nameplate from "./Nameplate";

const FREE_TABS = [
  { key: "skin", label: "Skin" },
  { key: "hair", label: "Hair" },
  { key: "face", label: "Face" },
  { key: "outfit", label: "Outfit" },
];
const SHOP_TABS = [
  { key: "hats", label: "Hats" },
  { key: "glasses", label: "Glasses" },
  { key: "beard", label: "Beard" },
  { key: "outfits", label: "Outfits" },
];

const Swatch = ({ color, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-7 h-7 rounded-full border-2 ${active ? "border-black scale-110" : "border-white/60"} transition-transform`}
    style={{ backgroundColor: `#${color}` }}
  />
);

const OptionThumb = ({ uri, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`p-0.5 rounded-lg border-2 ${active ? "border-[#7B0099] bg-purple-100" : "border-gray-300 bg-white"} hover:border-[#7B0099]`}
  >
    <img src={uri} alt="option" className="w-12 h-12" />
  </button>
);

const AvatarBuilder = ({ onClose }) => {
  const { token } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [options, setOptions] = useState(DEFAULT_AVATAR_OPTIONS);
  const [owned, setOwned] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [nameplate, setNameplate] = useState("none");
  const [fchips, setFchips] = useState(0);
  const [tab, setTab] = useState("skin");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const [cfg, cur] = await Promise.all([
          axios.get(`${API}/avatar/config`, { headers }),
          axios.get(`${API}/avatar`, { headers }),
        ]);
        setConfig(cfg.data);
        setFchips(cfg.data.fchips);
        setOwned(cur.data.owned_items || []);
        setEnabled(cur.data.enabled !== false);
        setNameplate(cur.data.nameplate || "none");
        setOptions({ ...cfg.data.default_options, ...(cur.data.options || {}) });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load avatar builder");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpt = (k, v) => setOptions((o) => ({ ...o, [k]: v }));
  const previewUri = useMemo(() => avatarDataUri(options, 256), [options]);

  const shopByCat = useMemo(() => {
    const m = { hats: [], glasses: [], beard: [], outfits: [] };
    (config?.shop || []).forEach((i) => m[i.category]?.push(i));
    return m;
  }, [config]);

  const isItemUnlocked = (item) => item.owned || (item.vip_only && !item.locked) || owned.includes(item.id);

  const buy = async (item) => {
    try {
      const res = await axios.post(`${API}/avatar/purchase`, { item_id: item.id }, { headers });
      if (res.data.new_balance !== undefined) setFchips(res.data.new_balance);
      setOwned(res.data.owned_items || [...owned, item.id]);
      toast.success(`Unlocked ${item.label}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/avatar`, { options, enabled, nameplate }, { headers });
      toast.success(enabled ? "Avatar saved! 🎉" : "Avatar hidden.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save avatar");
    } finally {
      setSaving(false);
    }
  };

  const fo = config?.free_options || {};

  return (
    <div className="fixed inset-0 z-[9800] bg-black/50 flex items-center justify-center p-3" onClick={onClose} data-testid="avatar-builder-modal">
      <div className="retro-window w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="retro-title-bar">
          <span>🎨 Avatar Creator</span>
          <button className="retro-button px-2 py-0 text-xs" onClick={onClose} data-testid="avatar-close"><X className="w-3 h-3" /></button>
        </div>

        {loading ? (
          <div className="p-8 text-center bg-[#C0C0C0] font-arial text-sm">Loading avatar builder…</div>
        ) : (
          <div className="flex flex-col md:flex-row bg-[#C0C0C0] overflow-hidden">
            {/* Preview */}
            <div className="md:w-56 flex-shrink-0 p-4 flex flex-col items-center border-r border-gray-400">
              <img src={previewUri} alt="avatar preview" className="w-40 h-40 bg-white rounded-xl border-2 border-gray-500" data-testid="avatar-preview" />
              <div className="mt-3 flex items-center gap-1 text-sm font-bold text-[#B8860B]" data-testid="avatar-fchips">
                <Coins className="w-4 h-4" /> {fchips} F-Chips
              </div>
              {config?.discount_percent > 0 && (
                <div className="mt-1 text-[11px] text-green-700 font-bold text-center">
                  {config.is_elite ? "Elite VIP" : "VIP"} {config.discount_percent}% shop discount!
                </div>
              )}
              <label className="mt-3 flex items-center gap-2 text-xs font-arial cursor-pointer">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="avatar-enabled-toggle" />
                Show my avatar
              </label>
              <button className="retro-button w-full mt-3 py-1.5 bg-[#7B0099] text-white border-[#7B0099] font-bold" onClick={save} disabled={saving} data-testid="avatar-save-btn">
                {saving ? "Saving…" : "Save Avatar"}
              </button>
            </div>

            {/* Editor */}
            <div className="flex-1 flex flex-col min-w-0">
              {/* Tabs */}
              <div className="flex flex-wrap gap-1 p-2 border-b border-gray-400">
                {FREE_TABS.map((t) => (
                  <button key={t.key} onClick={() => setTab(t.key)} data-testid={`avatar-tab-${t.key}`}
                    className={`retro-button text-xs px-2 py-1 ${tab === t.key ? "bg-blue-200" : ""}`}>{t.label}</button>
                ))}
                <span className="w-px bg-gray-400 mx-1" />
                {SHOP_TABS.map((t) => (
                  <button key={t.key} onClick={() => setTab(t.key)} data-testid={`avatar-tab-${t.key}`}
                    className={`retro-button text-xs px-2 py-1 ${tab === t.key ? "bg-amber-200" : ""}`}>🛒 {t.label}</button>
                ))}
                {config?.is_vip && (
                  <button onClick={() => setTab("badge")} data-testid="avatar-tab-badge"
                    className={`retro-button text-xs px-2 py-1 ${tab === "badge" ? "bg-purple-200" : ""}`}>✨ Badge</button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-3" style={{ maxHeight: "60vh" }}>
                {/* FREE: Skin */}
                {tab === "skin" && (
                  <div>
                    <p className="font-verdana text-xs font-bold mb-2">Skin Tone</p>
                    <div className="flex flex-wrap gap-2">
                      {(fo.skinColor || []).map((c) => (
                        <Swatch key={c} color={c} active={options.skinColor === c} onClick={() => setOpt("skinColor", c)} testid={`skin-${c}`} />
                      ))}
                    </div>
                  </div>
                )}

                {/* FREE: Hair */}
                {tab === "hair" && (
                  <div>
                    <p className="font-verdana text-xs font-bold mb-2">Hair Color</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(fo.hairColor || []).map((c) => (
                        <Swatch key={c} color={c} active={options.hairColor === c} onClick={() => setOpt("hairColor", c)} testid={`haircolor-${c}`} />
                      ))}
                    </div>
                    <p className="font-verdana text-xs font-bold mb-2">Hair Style</p>
                    <div className="grid grid-cols-5 gap-2">
                      {(fo.top || []).map((v) => (
                        <OptionThumb key={v} uri={avatarDataUri({ ...options, top: v }, 64)} active={options.top === v} onClick={() => setOpt("top", v)} testid={`hair-${v}`} />
                      ))}
                    </div>
                  </div>
                )}

                {/* FREE: Face */}
                {tab === "face" && (
                  <div className="space-y-3">
                    <div>
                      <p className="font-verdana text-xs font-bold mb-1">Eyes</p>
                      <div className="grid grid-cols-6 gap-2">
                        {(fo.eyes || []).map((v) => (
                          <OptionThumb key={v} uri={avatarDataUri({ ...options, eyes: v }, 64)} active={options.eyes === v} onClick={() => setOpt("eyes", v)} testid={`eyes-${v}`} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-verdana text-xs font-bold mb-1">Eyebrows</p>
                      <div className="grid grid-cols-6 gap-2">
                        {(fo.eyebrows || []).map((v) => (
                          <OptionThumb key={v} uri={avatarDataUri({ ...options, eyebrows: v }, 64)} active={options.eyebrows === v} onClick={() => setOpt("eyebrows", v)} testid={`brows-${v}`} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-verdana text-xs font-bold mb-1">Mouth</p>
                      <div className="grid grid-cols-6 gap-2">
                        {(fo.mouth || []).map((v) => (
                          <OptionThumb key={v} uri={avatarDataUri({ ...options, mouth: v }, 64)} active={options.mouth === v} onClick={() => setOpt("mouth", v)} testid={`mouth-${v}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* FREE: Outfit */}
                {tab === "outfit" && (
                  <div>
                    <p className="font-verdana text-xs font-bold mb-2">Shirt Color</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(fo.clothesColor || []).map((c) => (
                        <Swatch key={c} color={c} active={options.clothesColor === c} onClick={() => setOpt("clothesColor", c)} testid={`clothescolor-${c}`} />
                      ))}
                    </div>
                    <p className="font-verdana text-xs font-bold mb-2">Basic Shirts (free)</p>
                    <div className="grid grid-cols-5 gap-2">
                      {(fo.clothing || []).map((v) => (
                        <OptionThumb key={v} uri={avatarDataUri({ ...options, clothing: v }, 64)} active={options.clothing === v} onClick={() => setOpt("clothing", v)} testid={`clothing-${v}`} />
                      ))}
                    </div>
                  </div>
                )}

                {/* SHOP tabs */}
                {SHOP_TABS.some((t) => t.key === tab) && (
                  <div>
                    {(tab === "glasses" || tab === "beard") && (
                      <button
                        onClick={() => setOpt(tab === "glasses" ? "accessories" : "facialHair", "none")}
                        className="retro-button text-xs px-2 py-1 mb-3"
                        data-testid={`shop-none-${tab}`}
                      >
                        ✕ None
                      </button>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {shopByCat[tab].map((item) => {
                        const unlocked = isItemUnlocked(item);
                        const equipped = options[item.option] === item.value;
                        return (
                          <div key={item.id} className={`border-2 rounded-lg p-2 bg-white flex flex-col items-center ${equipped ? "border-[#7B0099]" : "border-gray-300"}`} data-testid={`shop-item-${item.id}`}>
                            <img src={avatarDataUri({ ...options, [item.option]: item.value }, 96)} alt={item.label} className="w-16 h-16" />
                            <p className="text-[11px] font-arial text-center mt-1 leading-tight h-7 flex items-center">{item.label}</p>
                            {item.locked ? (
                              <span className="text-[10px] text-red-600 font-bold flex items-center gap-0.5"><Lock className="w-3 h-3" /> VIP only</span>
                            ) : unlocked ? (
                              <button
                                onClick={() => setOpt(item.option, item.value)}
                                disabled={equipped}
                                className={`retro-button text-[11px] px-2 py-0.5 mt-0.5 ${equipped ? "bg-green-200" : ""}`}
                                data-testid={`equip-${item.id}`}
                              >
                                {equipped ? <><Check className="w-3 h-3 inline" /> Equipped</> : "Equip"}
                              </button>
                            ) : (
                              <button
                                onClick={() => buy(item)}
                                className="retro-button text-[11px] px-2 py-0.5 mt-0.5 bg-amber-200 font-bold flex items-center gap-0.5"
                                data-testid={`buy-${item.id}`}
                              >
                                <Coins className="w-3 h-3" /> {item.effective_price}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* VIP Badge / Nameplate tab */}
                {tab === "badge" && (
                  <div>
                    <p className="font-verdana text-xs font-bold mb-2">Animated Nameplate (VIP &amp; Elite exclusive)</p>
                    <div className="space-y-2">
                      {(config?.nameplates || []).map((n) => (
                        <button
                          key={n.id}
                          disabled={n.locked}
                          onClick={() => !n.locked && setNameplate(n.id)}
                          data-testid={`nameplate-opt-${n.id}`}
                          className={`w-full flex items-center justify-between border-2 rounded-lg px-3 py-2 bg-white ${nameplate === n.id ? "border-[#7B0099]" : "border-gray-300"} ${n.locked ? "opacity-50 cursor-not-allowed" : "hover:border-[#7B0099]"}`}
                        >
                          <span className="font-arial text-xs flex items-center gap-2">
                            {n.id === "none" ? "None" : <>Preview: <Nameplate nameplate={n.id} /></>}
                          </span>
                          <span className="text-[10px]">
                            {n.locked ? (n.tier === "elite" ? "🔒 Elite only" : "🔒 VIP only") : (nameplate === n.id ? "✓ Equipped" : "Select")}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarBuilder;
